import React, { useEffect, useState, useMemo } from 'react';
import { MainLayout } from '../components/layouts/MainLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { DataTable } from '../components/ui/data-table';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { applicationsAPI, usersAPI } from '../lib/firestore';
import { useToast } from '../components/ui/use-toast';
import type { Application, User } from '../types';

interface AdminInfo {
  email: string;
  fullName: string;
  applicationCount: number;
  applications: { id: string; name: string }[];
}

export const AdminsPage: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminInfo | null>(null);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [selectedAppToRemove, setSelectedAppToRemove] = useState<{ id: string; name: string } | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [appsData, usersData] = await Promise.all([
        applicationsAPI.getAll(),
        usersAPI.getAll(),
      ]);
      setApplications(appsData);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate admin information
  const adminsData = useMemo(() => {
    const adminMap = new Map<string, AdminInfo>();

    applications.forEach((app) => {
      app.admins.forEach((adminEmail) => {
        if (!adminMap.has(adminEmail)) {
          const user = users.find((u) => u.email === adminEmail);
          adminMap.set(adminEmail, {
            email: adminEmail,
            fullName: user?.fullName || adminEmail,
            applicationCount: 0,
            applications: [],
          });
        }

        const adminInfo = adminMap.get(adminEmail)!;
        adminInfo.applicationCount++;
        adminInfo.applications.push({ id: app.id, name: app.name });
      });
    });

    return Array.from(adminMap.values()).sort((a, b) => b.applicationCount - a.applicationCount);
  }, [applications, users]);

  const handleRemoveAdmin = async () => {
    if (!selectedAdmin || !selectedAppToRemove) return;

    try {
      const app = applications.find((a) => a.id === selectedAppToRemove.id);
      if (!app) return;

      const updatedAdmins = app.admins.filter((email) => email !== selectedAdmin.email);
      await applicationsAPI.update(app.id, { admins: updatedAdmins });

      setIsRemoveDialogOpen(false);
      setSelectedAdmin(null);
      setSelectedAppToRemove(null);
      await loadData();

      toast({
        title: 'Success',
        description: `Removed ${selectedAdmin.email} from ${selectedAppToRemove.name}`,
        className: 'bg-green-50 border-green-200',
      });
    } catch (error) {
      console.error('Error removing admin:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove admin',
        className: 'bg-red-50 border-red-200',
      });
    }
  };

  const openRemoveDialog = (admin: AdminInfo, app: { id: string; name: string }) => {
    setSelectedAdmin(admin);
    setSelectedAppToRemove(app);
    setIsRemoveDialogOpen(true);
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Admins Management</h1>
            <p className="text-muted-foreground mt-1">
              Manage application administrators across all applications
            </p>
          </div>
        </div>

        {loading ? (
          <div>Loading admins...</div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>All Admins ({adminsData.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                data={adminsData}
                columns={[
                  {
                    key: 'email',
                    label: 'Email',
                    sortable: true,
                    render: (admin) => <span className="font-medium">{admin.email}</span>,
                  },
                  {
                    key: 'fullName',
                    label: 'Full Name',
                    sortable: true,
                    render: (admin) => (
                      <span className={admin.fullName === admin.email ? 'text-muted-foreground' : ''}>
                        {admin.fullName}
                      </span>
                    ),
                  },
                  {
                    key: 'applicationCount',
                    label: 'Applications',
                    sortable: true,
                    render: (admin) => (
                      <Badge variant="outline">{admin.applicationCount}</Badge>
                    ),
                  },
                  {
                    key: 'applications',
                    label: 'Application List',
                    render: (admin) => (
                      <div className="space-y-1">
                        {admin.applications.slice(0, 3).map((app) => (
                          <div key={app.id} className="flex items-center gap-2">
                            <span className="text-sm">{app.name}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => openRemoveDialog(admin, app)}
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                        {admin.applications.length > 3 && (
                          <details className="text-sm">
                            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                              +{admin.applications.length - 3} more applications
                            </summary>
                            <div className="mt-2 space-y-1">
                              {admin.applications.slice(3).map((app) => (
                                <div key={app.id} className="flex items-center gap-2">
                                  <span className="text-sm">{app.name}</span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => openRemoveDialog(admin, app)}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    ),
                  },
                ]}
                searchKeys={['email', 'fullName']}
                emptyMessage="No admins found. Admins will appear here when applications are assigned to users."
                pageSize={25}
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Remove Admin Dialog */}
      <Dialog open={isRemoveDialogOpen} onOpenChange={setIsRemoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Admin from Application</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {selectedAdmin?.email} from{' '}
              {selectedAppToRemove?.name}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRemoveDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRemoveAdmin}>
              Remove Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
